import type { User } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { DISCOVERY_MAX_PROFILES, DISCOVERY_PAGE_SIZE } from '@/constants/discoveryFilters';
import type { GeoCursor } from '@/lib/geoQuery';
import {
  dedupeProfiles,
  discoveryService,
  favoriteService,
  type DiscoveryFeedPage,
} from '@/services';
import { useUIStore } from '@/stores';
import { UserProfile } from '@/types';
import { getDistanceToProfile, UNKNOWN_DISTANCE_KM } from '@/utils/geo';
import { useFeatureFlag } from './useFeatureFlag';
import { useFilters } from './useFilters';
import { useViewerLocation } from './useViewerLocation';

const COMBINED_RECENCY_DISTANCE_SORT = 'combined_recency_distance_sort';

// How much distance (in km) one minute of inactivity is worth when the
// last-active sort is enabled. A higher number makes recency more important.
const RECENCY_WEIGHT = 0.05;

type PageParam = {
  /** Per-bound resume state; null fetches every bound from its start. */
  cursors: GeoCursor[] | null;
};

const initialPageParam: PageParam = { cursors: null };

const EMPTY_PAGE: DiscoveryFeedPage = { profiles: [], cursors: [], hasMore: false };

function scoreForProfile(
  profile: UserProfile,
  viewerLocation: { latitude?: number; longitude?: number } | null,
  locationEnabled: boolean,
  useLastActive: boolean
): number {
  if (!viewerLocation) return 0;

  const distance = locationEnabled
    ? getDistanceToProfile(viewerLocation, profile)
    : UNKNOWN_DISTANCE_KM;

  if (!useLastActive) return distance;

  const lastActive = profile.lastActiveAt ?? profile.lastLoggedInAt ?? profile.createdAt;
  const minutesAgo = Math.max(0, (Date.now() - new Date(lastActive).getTime()) / (1000 * 60));
  return distance + minutesAgo * RECENCY_WEIGHT;
}

export function useUsers(user: User | null, profile: UserProfile | null) {
  const queryClient = useQueryClient();
  const { location: viewerLocation, locationEnabled } = useViewerLocation();
  const filters = useFilters(user, profile);
  const { enabled: useLastActiveSort } = useFeatureFlag(COMBINED_RECENCY_DISTANCE_SORT);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    isFetchNextPageError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['discoveryFeed', user?.uid, filters],
    queryFn: async ({ pageParam }) => {
      if (!user) return EMPTY_PAGE;
      // Favorites feed: id-based fetch, unaffected by geo or preference filters.
      if (filters.favoritesOnly) {
        const uids = await favoriteService.getMyFavoriteUids(user.uid);
        return discoveryService.getFavoritesFeed(user.uid, uids, filters);
      }
      return discoveryService.getDiscoveryFeed(user.uid, filters, pageParam.cursors);
    },
    initialPageParam,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined;
      // Stop paginating once the pool hits the display cap — later rounds
      // would only fetch profiles that can never be shown.
      const loaded = dedupeProfiles(allPages.flatMap(page => page.profiles)).length;
      return loaded < DISCOVERY_MAX_PROFILES ? { cursors: lastPage.cursors } : undefined;
    },
    enabled: !!user && !!profile?.onboarded,
    staleTime: 30_000,
    // Pages can be numerous once the pool is exhausted into memory; the
    // realtime subscription keeps the head fresh, so remounting (e.g. back
    // from a profile) must not re-read every loaded page.
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Keep fetching rounds in the background until every geohash bound reports
  // done or the pool reaches DISCOVERY_MAX_PROFILES, so all bound pages land
  // in memory instead of being dropped once a bound's first page is fetched.
  useEffect(() => {
    // isFetching also covers the initial fetch and refetches — a next-page
    // fetch started while one is in flight would cancel it (cancelRefetch)
    // and re-read every bound from its start.
    if (isFetching || isError || isFetchNextPageError || !hasNextPage || isFetchingNextPage) {
      return;
    }
    void fetchNextPage();
  }, [isFetching, isError, isFetchNextPageError, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    // No geo subscription for the favorites feed.
    if (!user || !profile?.onboarded || filters.favoritesOnly) return;

    const unsubscribe = discoveryService.subscribeToDiscoveryFeed(user.uid, filters, profiles => {
      queryClient.setQueryData<InfiniteData<DiscoveryFeedPage>>(
        ['discoveryFeed', user.uid, filters],
        old => {
          if (!old) {
            // Seed the cache so the grid renders before the first fetch
            // lands. hasMore stays false until the real first page reports
            // actual resume state — a true here could make the auto-fetch
            // effect fire fetchNextPage and cancel the in-flight initial
            // fetch, doubling the first round's reads.
            return {
              pages: [{ profiles, cursors: [], hasMore: false }],
              pageParams: [initialPageParam],
            };
          }
          const pages = [...old.pages];
          // The subscription only covers the head range; keep the first
          // page's resume state so pagination continues where it left off.
          pages[0] = { ...pages[0], profiles };
          return { ...old, pages };
        }
      );
    });

    return unsubscribe;
  }, [user, profile?.onboarded, filters, queryClient]);

  // Kept in a store (not local state) so the loaded range survives navigating
  // to a profile and back.
  const displayCount = useUIStore(s => s.discoveryDisplayCount);
  const setDisplayCount = useUIStore(s => s.setDiscoveryDisplayCount);

  // A changed filter set produces a new pool — restart the revealed range.
  // Compared by value (the filters object identity isn't stable while block
  // lists load) and skipped on mount so navigating back keeps the loaded set.
  const filtersKey = JSON.stringify(filters);
  const prevFiltersKey = useRef<string | null>(null);
  useEffect(() => {
    if (prevFiltersKey.current !== null && prevFiltersKey.current !== filtersKey) {
      setDisplayCount(DISCOVERY_PAGE_SIZE);
    }
    prevFiltersKey.current = filtersKey;
  }, [filtersKey, setDisplayCount]);

  const sortedProfiles = useMemo(() => {
    const all = dedupeProfiles(data?.pages.flatMap(page => page.profiles) ?? []);
    // Sort before slicing so the cap keeps the best-ranked profiles, not
    // whichever happened to arrive in earlier pages.
    return all
      .sort(
        (a, b) =>
          scoreForProfile(a, viewerLocation, locationEnabled, useLastActiveSort) -
          scoreForProfile(b, viewerLocation, locationEnabled, useLastActiveSort)
      )
      .slice(0, DISCOVERY_MAX_PROFILES);
  }, [data, viewerLocation, locationEnabled, useLastActiveSort]);

  const users = useMemo(
    () => sortedProfiles.slice(0, displayCount),
    [sortedProfiles, displayCount]
  );

  // loadMore shows the next DISCOVERY_PAGE_SIZE profiles from the already-loaded pool,
  // or fetches the next backend page if everything loaded is already visible.
  const loadMore = useCallback(async () => {
    const nextCount = Math.min(displayCount + DISCOVERY_PAGE_SIZE, sortedProfiles.length);
    if (nextCount > displayCount) {
      setDisplayCount(nextCount);
      return;
    }
    if (hasNextPage) {
      await fetchNextPage();
      setDisplayCount(displayCount + DISCOVERY_PAGE_SIZE);
    }
  }, [displayCount, sortedProfiles.length, hasNextPage, fetchNextPage, setDisplayCount]);

  return {
    users,
    isLoading,
    isError,
    error: error ?? null,
    refetch,
    isLoadingMore: isFetchingNextPage,
    hasMore: displayCount < sortedProfiles.length || hasNextPage,
    loadMore,
  };
}
